import olMap from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Style from 'ol/style/Style';
import Icon from 'ol/style/Icon';
import Circle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import { Coordinate } from 'ol/coordinate';
import { GpxLayerZIndex } from './GpxLayerOrder';

/**
 * Chequered finish flag, drawn as a data URI so it needs no icon font or asset
 * request. 4x3 squares of 5px on a white ground, on a dark pole whose base sits
 * on the coordinate.
 */
const FLAG_WIDTH = 26;
const FLAG_HEIGHT = 34;
const CHEQUER_SIZE = 5;
const BLACK_SQUARES: Array<[number, number]> = [[4, 4], [14, 4], [9, 9], [19, 9], [4, 14], [14, 14]];

function buildFinishFlagDataUri(): string {
    const squares = BLACK_SQUARES
        .map(([x, y]) => `<rect x="${x}" y="${y}" width="${CHEQUER_SIZE}" height="${CHEQUER_SIZE}" fill="#212529"/>`)
        .join('');
    const svg =
        `<svg xmlns="http://www.w3.org/2000/svg" width="${FLAG_WIDTH}" height="${FLAG_HEIGHT}" viewBox="0 0 ${FLAG_WIDTH} ${FLAG_HEIGHT}">` +
        `<rect x="2" y="2" width="2.5" height="30" rx="1.25" fill="#212529" stroke="#ffffff" stroke-width="0.75"/>` +
        `<rect x="4" y="4" width="20" height="15" fill="#ffffff" stroke="#212529" stroke-width="1"/>` +
        squares +
        `</svg>`;
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
}

/** Green "you start here" dot: no route colour, so it reads the same on every route. */
const START_STYLE = new Style({
    image: new Circle({
        radius: 7,
        fill: new Fill({ color: '#1a7f37' }),
        stroke: new Stroke({ color: '#ffffff', width: 2.5 })
    })
});

const FINISH_STYLE = new Style({
    image: new Icon({
        src: buildFinishFlagDataUri(),
        //the pole base, not the middle of the image, marks the spot
        anchor: [3 / FLAG_WIDTH, 1],
        anchorXUnits: 'fraction' as any,
        anchorYUnits: 'fraction' as any
    })
});

/**
 * Shows where the route being edited starts and finishes. Which end is which
 * matters during a session — Extend continues from the finish, and Reverse swaps
 * the two — so the markers are the feedback for both tools.
 *
 * Display only: no interactions are attached, and the layer sits below the
 * waypoint markers so it can't swallow a click meant for one.
 */
export class GpxEditEndpointLayer {

    private source: VectorSource = new VectorSource();
    private layer: VectorLayer;

    public constructor(private map: olMap) {
        this.layer = new VectorLayer({
            source: this.source,
            style: (feature) => feature.get('isStart') ? START_STYLE : FINISH_STYLE,
            zIndex: GpxLayerZIndex.editEndpoints
        });
        this.layer.set('id', 'gpx-edit-endpoints', true);
        this.layer.setVisible(false);
        this.map.addLayer(this.layer);
    }

    /**
     * Places the markers at the ends of the route. `segments` is the ordered list
     * of segment coordinate arrays, so the first coordinate of the first segment is
     * the start and the last of the last is the finish. Empty input clears them,
     * and a single-point route shows only the start.
     */
    public update(segments: Coordinate[][]): void {
        this.source.clear();

        const usable = segments.filter(coords => coords.length > 0);
        if (usable.length === 0) {
            this.layer.setVisible(false);
            return;
        }

        const start = usable[0][0];
        const lastSegment = usable[usable.length - 1];
        const finish = lastSegment[lastSegment.length - 1];

        this.source.addFeature(this.createMarker(start, true));
        //a route still being drawn can be a single point — one marker, not two
        if (finish !== start) {
            this.source.addFeature(this.createMarker(finish, false));
        }
        this.layer.setVisible(true);
    }

    /** Removes the markers (the session ended). */
    public clear(): void {
        this.source.clear();
        this.layer.setVisible(false);
    }

    private createMarker(coordinate: Coordinate, isStart: boolean): Feature {
        const feature = new Feature({ geometry: new Point(coordinate) });
        feature.set('isStart', isStart);
        return feature;
    }
}
